import {
    CHANGE_LOCATION,
    YOUR_DETECTED_LOCATION,
    YOUR_SELECTED_LOCATION,
    LOCAL_CITY_LOCATION,
    NATIONAL_CALL_CENTER_DISPLAY,
    NATIONAL_CALL_CENTER_LOCATION,
    PHONE_NUMBER,
    PHONE_NUMBER_DISPLAY,
    AVERAGE_RATE_THRESHOLD
} from '@constants/constants';
import { useLocator } from '@contexts/franchise/locator-provider';
import formatPhoneLink from '@utils/format-phone-link';
import React from 'react';
import { useCtaProvider } from '../../contexts/franchise/cta-provider';
import { useMicrosite } from '../../contexts/franchise/microsite-context';
import { RatingSummary } from '../atoms/RatingSummary';
import Color from '../primitives/colors';
import Skeleton from '../primitives/skeleton';
import RowSet from '../primitives/grid/row-set';
import Typography from '../primitives/typography';
import StyledPillButton, { StyledPillButtonAsLink } from '../primitives/styled-pill-button';
import { StyledButtonAsLink } from '../primitives/styled-button';
import triggerInvoca from '../utils/trigger-invoca';
import ChangeLocationDrawer from '../franchise/change-location-drawer';
import PhoneNumber from '../molecules/phone-number';

// TODO: Typings
const HeaderDropdown = (): JSX.Element => {
    const {
        loading: geoIsLoading,
        geo: locatorGeo,
        franchise: locatorFranchise,
        locationChangedByUser,
        isNationalCallCenter,
    } = useLocator();

    React.useEffect(() => {
        triggerInvoca();
    }, []);

    const [locationChangeVisible, setLocationChangeVisible] = React.useState<boolean>(false);

    const onChangeLocation = (): void => {
        setLocationChangeVisible(true);
    };

    const { hideFranchiseLink } = useCtaProvider();
    const { isLocalCityPage, franchise: micrositeFranchise } = useMicrosite();

    const geo = locatorGeo;
    const franchise = micrositeFranchise || locatorFranchise?.data;
    const disableChangeLocation = !!micrositeFranchise;

    const frId = franchise?.franchiseNumber || franchise?.FranchiseNumber;
    const frLink = franchise?.permalink;
    const reviewCount = franchise?.reviewCount || franchise?.review_count;
    const averageRating = franchise?.averageRating || franchise?.average_rating;
    const { name: frName, mainPhone: frPhone } = franchise;

    return (
        <div css={{
            padding: '16px',
            width: 'min-content',
            maxWidth: '700px',
        }}
        >
            <ChangeLocationDrawer
                visible={locationChangeVisible}
                onClose={() => setLocationChangeVisible(false)}
            />
            <div>
                <div css={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                }}
                >
                    <RowSet breakpoints={{ dt: { between: 20 } }}>
                        <RowSet
                            breakpoints={{ dt: { between: 4 } }}
                            css={{
                                paddingRight: '16px',
                            }}
                        >
                            <Color color="primaryOrange">
                                {(isLocalCityPage && !hideFranchiseLink) && LOCAL_CITY_LOCATION}
                                {(locationChangedByUser && !isLocalCityPage
                                        && !hideFranchiseLink && !isNationalCallCenter)
                                    && YOUR_SELECTED_LOCATION}
                                {(!locationChangedByUser && !isLocalCityPage
                                        && !hideFranchiseLink && !isNationalCallCenter)
                                    && YOUR_DETECTED_LOCATION}
                                {((hideFranchiseLink || isNationalCallCenter)
                                    && !isLocalCityPage) && NATIONAL_CALL_CENTER_LOCATION}
                            </Color>
                            {(!isNationalCallCenter && !isLocalCityPage && !locationChangedByUser) && (
                                <Skeleton css={{ height: '32px' }}>
                                    {(!geoIsLoading
                                        && geo?.city
                                        && !hideFranchiseLink
                                        && frName) && (
                                        <Typography fontSize="3xl" fontWeight="medium">
                                            {geo.city}
                                            {geo?.stateShort ? ', ' : ' '}
                                            {geo?.stateShort || ''}
                                        </Typography>
                                    )}
                                </Skeleton>
                            )}
                            {(!isNationalCallCenter && !isLocalCityPage && locationChangedByUser) && (
                                <Skeleton css={{ height: '32px' }}>
                                    {(!geoIsLoading
                                        && geo?.city
                                        && !hideFranchiseLink
                                        && frName) && (
                                        <Typography fontSize="3xl" fontWeight="medium">
                                            {frName}
                                        </Typography>
                                    )}
                                </Skeleton>
                            )}
                            {isLocalCityPage && (
                                <Skeleton css={{ height: '32px' }}>
                                    <Typography fontSize="3xl" fontWeight="medium">
                                        {franchise?.address?.city}
                                        {franchise?.address?.state ? ', ' : ' '}
                                        {franchise?.address?.state || ''}
                                    </Typography>
                                </Skeleton>
                            )}
                            {((hideFranchiseLink || isNationalCallCenter) && !isLocalCityPage) && (
                                <Typography fontSize="3xl" fontWeight="medium">
                                    {NATIONAL_CALL_CENTER_DISPLAY}
                                </Typography>
                            )}
                            {((!isNationalCallCenter && !hideFranchiseLink && !locationChangedByUser) || isLocalCityPage) && (
                                <Skeleton>
                                    {frName && <Typography fontSize="sm">{frName}</Typography>}
                                </Skeleton>
                            )}
                            {(reviewCount !== 0 && !hideFranchiseLink && !isNationalCallCenter && !!averageRating && averageRating >= AVERAGE_RATE_THRESHOLD) && (
                                <RatingSummary count={reviewCount} rating={averageRating} />
                            )}
                        </RowSet>
                        <Skeleton
                            css={{
                                width: '256px',
                                height: '48px',
                            }}
                            isButton
                            backgroundColor="primaryOrange"
                        >
                            {(frPhone && !hideFranchiseLink) && (
                                <PhoneNumber
                                    as={StyledButtonAsLink}
                                    to={`tel:${formatPhoneLink(frPhone) as string}`}
                                >
                                    {formatPhoneLink(frPhone)}
                                </PhoneNumber>
                            )}
                            {hideFranchiseLink && (
                                <PhoneNumber
                                    as={StyledButtonAsLink}
                                    to={`tel:${PHONE_NUMBER as string}`}
                                >
                                    {PHONE_NUMBER_DISPLAY}
                                </PhoneNumber>
                            )}
                        </Skeleton>
                    </RowSet>
                    <RowSet
                        breakpoints={{ dt: { between: 18 } }}
                        css={{
                            textAlign: 'center',
                            width: 'unset',
                        }}
                    >
                        {(!disableChangeLocation && !hideFranchiseLink && !isLocalCityPage) && (
                            <div>
                                Need help elsewhere?
                                {' '}
                                <StyledPillButton
                                    className="change-location-open-cta"
                                    onClick={onChangeLocation}
                                >
                                    {CHANGE_LOCATION}
                                    {' '}
                                    ›
                                </StyledPillButton>
                            </div>
                        )}
                        {isLocalCityPage && (
                            <div>
                                Need help elsewhere?
                                {' '}
                                <StyledPillButtonAsLink
                                    to="/locations"
                                >
                                    {CHANGE_LOCATION}
                                    {' '}
                                    ›
                                </StyledPillButtonAsLink>
                            </div>
                        )}
                        <Typography fontSize="xl" fontWeight="medium" color="primaryOrange">
                            24/7 Emergency Service
                        </Typography>
                        {!!frId && !hideFranchiseLink && (
                            <StyledPillButtonAsLink
                                to={frLink}
                            >
                                Find Out More ›
                            </StyledPillButtonAsLink>
                        )}
                    </RowSet>
                </div>
            </div>
        </div>
    );
};
export default HeaderDropdown;
